/**
 * Who is signed in, for the client.
 *
 * One query (`/api/auth/context`) is the source of truth, and everything
 * else derives from it -- including the admin surfaces, which used to be
 * gated by a `?superadmin=1` URL parameter anyone could type. That flag was
 * always presentation rather than access control and said so; this replaces
 * it with an account, and the server enforces the same line independently.
 */
import { createContext, use, useCallback, useEffect, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api.ts'

export type Role = 'admin' | 'viewer'

export type AuthUser = {
  id: string
  username: string
  role: Role
  displayName: string | null
  mustChangePassword: boolean
}

export type AuthContext = {
  user: AuthUser | null
  /** True while the first context request is in flight. */
  loading: boolean
  /** No accounts exist yet: the app shows the create-first-admin form. */
  setupRequired: boolean
  registration: 'closed' | 'invite' | 'request'
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const Ctx = createContext<AuthContext | null>(null)

type ContextResponse = {
  user: AuthUser | null
  setupRequired: boolean
  registration: 'closed' | 'invite' | 'request'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'context'],
    queryFn: () => api<ContextResponse>('/api/auth/context'),
    // The session can end server-side -- an admin disabling the account, a
    // password change elsewhere -- so this is re-checked rather than cached
    // for the life of the tab.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  })

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['auth', 'context'] })
  }, [qc])

  // Any 401 anywhere means the session this tab believed in is gone. Re-ask
  // rather than assume: the answer might be "signed out", or might be that
  // the request was simply for something this account may not have.
  useEffect(() => {
    const onUnauthorized = () => { void refresh() }
    window.addEventListener('explorer:unauthorized', onUnauthorized)
    return () => window.removeEventListener('explorer:unauthorized', onUnauthorized)
  }, [refresh])

  const logout = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => {})

    /*
     * Write the signed-out state *before* dropping anything.
     *
     * The first version called `qc.clear()` and then invalidated the auth
     * query -- but `clear()` removes that query too, and invalidating one
     * that is no longer in the cache refetches nothing. The observer kept
     * rendering its last value, so the header still said "signed in" until
     * something else forced a remount. Pressing the button appeared to do
     * nothing, which is the worst possible outcome for a sign-out.
     *
     * `setQueryData` flips it synchronously, so the gate re-renders on the
     * spot. Everything else is then removed rather than invalidated: it was
     * fetched as the previous user, and a corpus page rendering under the
     * next account even for one frame is the wrong thing to show.
     */
    qc.setQueryData<ContextResponse>(['auth', 'context'], (old) => ({
      setupRequired: old?.setupRequired ?? false,
      registration: old?.registration ?? 'closed',
      user: null,
    }))
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' })

    // And confirm it against the server, in case the session outlived the
    // request that was meant to end it.
    await refresh()
  }, [qc, refresh])

  const value: AuthContext = {
    user: data?.user ?? null,
    loading: isLoading,
    setupRequired: data?.setupRequired ?? false,
    registration: data?.registration ?? 'closed',
    refresh,
    logout,
  }

  return <Ctx value={value}>{children}</Ctx>
}

export function useAuth(): AuthContext {
  const c = use(Ctx)
  if (!c) throw new Error('useAuth outside AuthProvider')
  return c
}

/** Convenience for the many places that only care whether this is an admin. */
export function useIsAdmin(): boolean {
  return use(Ctx)?.user?.role === 'admin'
}
